import React from "react";
import {
	BrowserRouter as Router,
	Routes,
	Route,
	Link,
	useLocation,
	Outlet,
	useNavigate,
} from "react-router-dom";
import ShowGraph from "./ShowGraph";
import Tab1 from "./Tab1";
import BottomTab from "./BottomTab";
import "../styles/LandingPage.css";
import SidebarNav from "./SidebarNav";
import Dashboard from "./Dashboard";
import VideoScriptPage from "./VideoScriptPage";
import AvatarVideo from "./AvatarVideo";
import { 
	Paper, 
	Typography, 
	Box, 
	Container, 
	Grid, 
	Button,
	AppBar,
	Toolbar,
	useTheme,
	useMediaQuery,
	IconButton,
} from "@mui/material";
import {
	LocalHospital,
	Agriculture,
	AccountBalance,
	People,
	Business,
	Search,
	ArrowForward,
	TrendingUp,
	ConnectWithoutContact,
	Menu as MenuIcon,
} from "@mui/icons-material";
import { useAuth } from "react-oidc-context";

const LandingPage = () => {
	const location = useLocation();
	const navigate = useNavigate();
	const auth = useAuth();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("md"));

	const handleLoginClick = () => {
		auth.signinRedirect();
	};

	return (
		<div className="bg-gray-50 min-h-screen">
			{/* Navigation Bar */}
			<AppBar
				position="sticky"
				elevation={0}
				color="default"
				sx={{ backgroundColor: "white", borderBottom: "1px solid #e5e7eb" }}
			>
				<Container maxWidth="lg">
					<Toolbar sx={{ justifyContent: "space-between", py: 1 }}>
						<Typography
							variant="h6"
							component="div"
							sx={{
								color: theme.palette.primary.main,
								fontWeight: 700,
								display: "flex",
								alignItems: "center",
								cursor: "pointer",
							}}
							onClick={() => navigate("/")}
						>
							<Search sx={{ mr: 1 }} />
							introflow
						</Typography>

						{isMobile ? (
							<IconButton
								edge="end"
								color="inherit"
								aria-label="menu"
							>
								<MenuIcon />
							</IconButton>
						) : (
							<Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
								<Button 
									color="inherit" 
									onClick={() => {
										const productsSection = document.getElementById("products");
										if (productsSection) {
											productsSection.scrollIntoView({ behavior: "smooth" });
										}
									}}
									sx={{ color: "text.primary", textTransform: "none" }}
								>
									Products
								</Button>
								<Button 
									color="inherit" 
									onClick={() => navigate("/pay-for-outcome")}
									sx={{ color: "text.primary", textTransform: "none" }}
								>
									Pricing
								</Button>
								<Button
									variant="contained"
									color="primary"
									onClick={handleLoginClick}
									sx={{ textTransform: "none" }}
								>
									Login
								</Button>
							</Box>
						)}
					</Toolbar>
				</Container>
			</AppBar>

			<Container maxWidth="lg" className="py-8">
				{/* Hero Section */}
				<Box className="text-center mb-12 py-8 flex flex-col items-center">
					<Typography
						variant="h3"
						component="h1"
						className="font-bold text-gray-800 mb-4"
					>
						introflow
					</Typography>
					<Box className="flex flex-row gap-2 items-center justify-center mb-4">
						<Search className="text-blue-500 text-5xl" />
					</Box>
					<Typography
						variant="h5"
						className="text-gray-700 max-w-2xl mx-auto mb-6 font-medium"
					>
						Find out who you need to talk to
					</Typography>
					<Typography
						variant="body1"
						className="text-gray-600 max-w-2xl mx-auto mb-8"
					>
						Discover the right leads and connections across industries. 
						Connect with decision-makers, influencers, and key professionals 
						who matter to your business.
					</Typography>
				</Box>

				{/* Industry Cards */}
				<div id="products" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
					{/* Healthcare Card */}
					<Paper
						elevation={4}
						className="rounded-xl overflow-hidden h-80 cursor-pointer transform transition-all duration-300 ease-in-out hover:shadow-xl relative group border border-gray-100"
						onClick={() => navigate("/leads/healthcare")}
					>
						<Box className="absolute inset-0 bg-gradient-to-br from-red-500/10 to-red-700/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
						<Box className="bg-gradient-to-r from-red-500 to-red-600 p-4 text-white">
							<Typography variant="h5" className="font-bold text-center">
								Healthcare
							</Typography>
						</Box>
						<Box className="flex flex-col items-center justify-center h-4/5 p-6 relative">
							<LocalHospital className="text-6xl mb-6 text-gray-300 group-hover:text-red-500 transition-all duration-300" />
							<Typography
								variant="body1"
								className="text-center text-gray-600 group-hover:text-gray-800 transition-colors duration-300 mb-6"
							>
								Connect with healthcare professionals, medical practitioners, and industry leaders
							</Typography>
							<Button
								variant="outlined"
								color="error"
								endIcon={<ArrowForward />}
								className="mt-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
							>
								Explore Leads
							</Button>
						</Box>
						<Box className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-red-400 to-red-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
					</Paper>

					{/* Agriculture Card */}
					<Paper
						elevation={4}
						className="rounded-xl overflow-hidden h-80 cursor-pointer transform transition-all duration-300 ease-in-out hover:shadow-xl relative group border border-gray-100"
						onClick={() => navigate("/leads/agriculture")}
					>
						<Box className="absolute inset-0 bg-gradient-to-br from-green-500/10 to-green-700/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
						<Box className="bg-gradient-to-r from-green-500 to-green-600 p-4 text-white">
							<Typography variant="h5" className="font-bold text-center">
								Agriculture
							</Typography>
						</Box>
						<Box className="flex flex-col items-center justify-center h-4/5 p-6 relative">
							<Agriculture className="text-6xl mb-6 text-gray-300 group-hover:text-green-500 transition-all duration-300" />
							<Typography
								variant="body1"
								className="text-center text-gray-600 group-hover:text-gray-800 transition-colors duration-300 mb-6"
							>
								Find leads in farming, agribusiness, and agricultural technology sectors
							</Typography>
							<Button
								variant="outlined"
								color="success"
								endIcon={<ArrowForward />}
								className="mt-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
							>
								Explore Leads
							</Button>
						</Box>
						<Box className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-green-400 to-green-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
					</Paper>

					{/* Venture Capital Card */}
					<Paper
						elevation={4}
						className="rounded-xl overflow-hidden h-80 cursor-pointer transform transition-all duration-300 ease-in-out hover:shadow-xl relative group border border-gray-100"
						onClick={() => navigate("/leads/vc")}
					>
						<Box className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-blue-700/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
						<Box className="bg-gradient-to-r from-blue-500 to-blue-600 p-4 text-white">
							<Typography variant="h5" className="font-bold text-center">
								Venture Capital
							</Typography>
						</Box>
						<Box className="flex flex-col items-center justify-center h-4/5 p-6 relative">
							<AccountBalance className="text-6xl mb-6 text-gray-300 group-hover:text-blue-500 transition-all duration-300" />
							<Typography
								variant="body1"
								className="text-center text-gray-600 group-hover:text-gray-800 transition-colors duration-300 mb-6"
							>
								Connect with VCs, investors, and funding partners for your business
							</Typography>
							<Button
								variant="outlined"
								color="primary"
								endIcon={<ArrowForward />}
								className="mt-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
							>
								Explore Leads
							</Button>
						</Box>
						<Box className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-400 to-blue-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
					</Paper>

					{/* Social Media Influencer Card */}
					<Paper
						elevation={4}
						className="rounded-xl overflow-hidden h-80 cursor-pointer transform transition-all duration-300 ease-in-out hover:shadow-xl relative group border border-gray-100"
						onClick={() => navigate("/leads/influencers")}
					>
						<Box className="absolute inset-0 bg-gradient-to-br from-pink-500/10 to-pink-700/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
						<Box className="bg-gradient-to-r from-pink-500 to-pink-600 p-4 text-white">
							<Typography variant="h5" className="font-bold text-center">
								Social Media Influencers
							</Typography>
						</Box>
						<Box className="flex flex-col items-center justify-center h-4/5 p-6 relative">
							<People className="text-6xl mb-6 text-gray-300 group-hover:text-pink-500 transition-all duration-300" />
							<Typography
								variant="body1"
								className="text-center text-gray-600 group-hover:text-gray-800 transition-colors duration-300 mb-6"
							>
								Discover influencers and content creators across all social platforms
							</Typography>
							<Button
								variant="outlined"
								sx={{ 
									borderColor: '#ec4899', 
									color: '#db2777',
									'&:hover': {
										borderColor: '#ec4899',
										backgroundColor: 'rgba(236, 72, 153, 0.04)'
									}
								}}
								endIcon={<ArrowForward />}
								className="mt-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
							>
								Explore Leads
							</Button>
						</Box>
						<Box className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-pink-400 to-pink-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
					</Paper>

					{/* Business & Enterprise Card */}
					<Paper
						elevation={4}
						className="rounded-xl overflow-hidden h-80 cursor-pointer transform transition-all duration-300 ease-in-out hover:shadow-xl relative group border border-gray-100"
						onClick={() => navigate("/leads/business")}
					>
						<Box className="absolute inset-0 bg-gradient-to-br from-indigo-500/10 to-indigo-700/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
						<Box className="bg-gradient-to-r from-indigo-500 to-indigo-600 p-4 text-white">
							<Typography variant="h5" className="font-bold text-center">
								Business & Enterprise
							</Typography>
						</Box>
						<Box className="flex flex-col items-center justify-center h-4/5 p-6 relative">
							<Business className="text-6xl mb-6 text-gray-300 group-hover:text-indigo-500 transition-all duration-300" />
							<Typography
								variant="body1"
								className="text-center text-gray-600 group-hover:text-gray-800 transition-colors duration-300 mb-6"
							>
								Find decision-makers and executives in businesses of all sizes
							</Typography>
							<Button
								variant="outlined"
								color="primary"
								endIcon={<ArrowForward />}
								className="mt-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
							>
								Explore Leads
							</Button>
						</Box>
						<Box className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-indigo-400 to-indigo-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
					</Paper>

					{/* More Industries Card */}
					<Paper
						elevation={4}
						className="rounded-xl overflow-hidden h-80 cursor-pointer transform transition-all duration-300 ease-in-out hover:shadow-xl relative group border border-gray-100"
						onClick={() => navigate("/leads/all")}
					>
						<Box className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-purple-700/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
						<Box className="bg-gradient-to-r from-purple-500 to-purple-600 p-4 text-white">
							<Typography variant="h5" className="font-bold text-center">
								More Industries
							</Typography>
						</Box>
						<Box className="flex flex-col items-center justify-center h-4/5 p-6 relative">
							<Search className="text-6xl mb-6 text-gray-300 group-hover:text-purple-500 transition-all duration-300" />
							<Typography
								variant="body1"
								className="text-center text-gray-600 group-hover:text-gray-800 transition-colors duration-300 mb-6"
							>
								Explore leads across technology, finance, education, and many more industries
							</Typography>
							<Button
								variant="outlined"
								color="secondary"
								endIcon={<ArrowForward />}
								className="mt-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
							>
								Explore All
							</Button>
						</Box>
						<Box className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-400 to-purple-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
					</Paper>
				</div>

				{/* Feature Section */}
				<Box className="bg-white rounded-xl shadow-md p-8 mb-12">
					<Typography
						variant="h5"
						className="font-bold text-gray-800 mb-6 text-center"
					>
						Why Choose introflow?
					</Typography>

					<div className="flex flex-col md:flex-row justify-center items-center gap-8 w-full mx-auto">
						<Grid item xs={12} md={4} className="text-center">
							<Box className="p-4">
								<Search className="text-4xl text-blue-500 mb-4" />
								<Typography
									variant="h6"
									className="font-medium mb-2"
								>
									Smart Lead Discovery
								</Typography>
								<Typography
									variant="body2"
									className="text-gray-600"
								>
									Find the right contacts and decision-makers
									across multiple industries with precision
								</Typography>
							</Box>
						</Grid>

						<Grid item xs={12} md={4} className="text-center">
							<Box className="p-4">
								<ConnectWithoutContact className="text-4xl text-indigo-500 mb-4" />
								<Typography
									variant="h6"
									className="font-medium mb-2"
								>
									Direct Connections
								</Typography>
								<Typography
									variant="body2"
									className="text-gray-600"
								>
									Connect directly with the people who matter
									to your business growth
								</Typography>
							</Box>
						</Grid>

						<Grid item xs={12} md={4} className="text-center">
							<Box className="p-4">
								<TrendingUp className="text-4xl text-green-500 mb-4" />
								<Typography
									variant="h6"
									className="font-medium mb-2"
								>
									Industry Coverage
								</Typography>
								<Typography
									variant="body2"
									className="text-gray-600"
								>
									Access leads from healthcare, agriculture, VC,
									social media, and many more industries
								</Typography>
							</Box>
						</Grid>
					</div>
				</Box>
			</Container>
		</div>
	);
};

export default LandingPage;
